import {AfterViewInit, Component, ElementRef, EventEmitter, OnDestroy, Output, ViewChild} from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-signature-input',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './signature-input.component.html',
  styleUrls: ['./signature-input.component.scss']
})
export class SignatureInputComponent implements AfterViewInit, OnDestroy {
  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;
  @Output() signatureChange = new EventEmitter<string>();

  private ctx!: CanvasRenderingContext2D;
  private drawing = false;
  private lastX = 0;
  private lastY = 0;
  private rafId = 0;
  private resizeObserver?: ResizeObserver;

  ngAfterViewInit(): void {
    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d')!;
    this.ctx = ctx;

    // Style du trait
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2;

    this.resizeCanvasToDevicePixelRatio();

    // Écouteurs pointer (fonctionnent pour doigt, stylet, souris)
    canvas.addEventListener('pointerdown', this.onPointerDown, { passive: false });
    canvas.addEventListener('pointermove', this.onPointerMove, { passive: false });
    // Pointerup / cancel / leave sur le canvas et le document (au cas où le doigt sort)
    canvas.addEventListener('pointerup', this.onPointerUp);
    canvas.addEventListener('pointerleave', this.onPointerUp);
    canvas.addEventListener('pointercancel', this.onPointerUp);
    document.addEventListener('pointerup', this.onPointerUp);

    // OBSERVER: ajuste la résolution réelle si la taille CSS change
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.resizeCanvasToDevicePixelRatio());
      this.resizeObserver.observe(canvas);
    }

    // Empêche le scroll/zoom par geste pendant le dessin
    canvas.style.touchAction = 'none';
  }

  private emitSignature() {
    const base64 = this.canvasRef.nativeElement.toDataURL('image/png');
    this.signatureChange.emit(base64);
  }

  ngOnDestroy(): void {
    const canvas = this.canvasRef.nativeElement;
    canvas.removeEventListener('pointerdown', this.onPointerDown as any);
    canvas.removeEventListener('pointermove', this.onPointerMove as any);
    canvas.removeEventListener('pointerup', this.onPointerUp as any);
    canvas.removeEventListener('pointerleave', this.onPointerUp as any);
    canvas.removeEventListener('pointercancel', this.onPointerUp as any);
    document.removeEventListener('pointerup', this.onPointerUp as any);
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.rafId);
  }

  // Redimensionne le canvas en tenant compte du DPR (rétina)
  private resizeCanvasToDevicePixelRatio() {
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.max(1, window.devicePixelRatio || 1);

    const needResize = canvas.width !== Math.round(rect.width * dpr) ||
        canvas.height !== Math.round(rect.height * dpr);

    if (needResize) {
      const img = this.ctx?.getImageData?.(0, 0, canvas.width, canvas.height);
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      this.ctx.scale(dpr, dpr);
      // Repeindre un fond blanc si souhaité
      this.ctx.fillStyle = '#fff';
      this.ctx.fillRect(0, 0, rect.width, rect.height);
      if (img) {
        // Remise de l’image n’est pas triviale quand on change d’échelle,
        // on peut ignorer ou gérer un offscreen canvas si on veut conserver le tracé.
      }
    }
  }

  // Handlers (liés pour removeEventListener)
  private onPointerDown = (e: PointerEvent) => {
    e.preventDefault();
    const { x, y } = this.eventToCanvasXY(e);
    this.drawing = true;
    this.lastX = x;
    this.lastY = y;

    const pressure = e.pressure ?? 0.5;
    this.ctx.lineWidth = this.pressureToWidth(pressure);
  };

  private onPointerMove = (e: PointerEvent) => {
    if (!this.drawing) return;
    e.preventDefault();

    cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => {
      const { x, y } = this.eventToCanvasXY(e);

      this.ctx.beginPath();
      this.ctx.moveTo(this.lastX, this.lastY);
      this.ctx.lineTo(x, y);

      const pressure = e.pressure ?? 0.5;
      this.ctx.lineWidth = this.pressureToWidth(pressure);

      this.ctx.stroke();

      this.lastX = x;
      this.lastY = y;
    });
  };

  private onPointerUp = (_e: PointerEvent) => {
    if (!this.drawing) return;
    this.drawing = false;
    cancelAnimationFrame(this.rafId);
    this.emitSignature();
  };

  private eventToCanvasXY(e: PointerEvent) {
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();

    // Coordonnées CSS -> pixels du canvas (ici le contexte est déjà scale(dpr, dpr))
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    return { x, y };
  }

  private pressureToWidth(pressure: number) {
    // plage 1.5px à 3.5px par exemple
    const min = 1.5, max = 3.5;
    return min + (max - min) * Math.min(Math.max(pressure, 0), 1);
  }

  public clear() {
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    this.ctx.clearRect(0, 0, rect.width, rect.height);
    this.signatureChange.emit(undefined);
  }
}
